import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Menu,
  MenuItem,
  Skeleton,
  TextField,
  useMediaQuery,
} from "@mui/material";
import {
  CalendarMonthOutlined,
  CheckCircleOutline,
  ExpandLessOutlined,
  ExpandMoreOutlined,
  GroupsOutlined,
  InfoOutlined,
  MenuBookOutlined,
  MoreVertOutlined,
  SchoolOutlined,
  SearchOutlined,
  TuneOutlined,
  VisibilityOutlined,
} from "@mui/icons-material";
import ConfirmDialog from "components/atoms/ConfirmDialog/ConfirmDialog";
import SearchSelect from "components/atoms/SearchSelect/SearchSelect";
import SelectField from "components/atoms/SelectField/SelectField";
import {
  findAcaoQualificacao,
  findTurmasDisponiveisAcao,
  realizarInscricaoTurma,
} from "API/apiAcoes";
import {
  AcaoDisponivelInscricao,
  AcaoQualificacao,
  TurmaDisponivelInscricao,
  TurmaDisponivelInscricaoDetalhe,
} from "types/acao";
import { formatarDataDbParaBr } from "utils/Masks";
import { useAcoesDisponiveis } from "./hooks/useAcoesDisponiveis";
import stylesButton from "styles/Button.module.css";
import stylesInput from "styles/Input.module.css";
import stylesManagement from "styles/ManagementList.module.css";
import styles from "./AcoesDisponiveis.module.css";

const orderOptions = [
  { value: "mais_recentes", label: "Mais recentes" },
  { value: "mais_antigas", label: "Mais antigas" },
  { value: "nome_az", label: "Nome da ação - A a Z" },
  { value: "nome_za", label: "Nome da ação - Z a A" },
  { value: "inscricoes_encerrando", label: "Inscrições encerrando primeiro" },
  { value: "inicio_proximo", label: "Data de início mais próxima" },
];

const getTurmaDate = (turma: TurmaDisponivelInscricao) =>
  String(turma.data_inicio ?? turma.dataInicio ?? "");
const getTurmaLabel = (turma: TurmaDisponivelInscricao) =>
  String(turma.nome ?? turma.tema ?? "Turma disponível");

const AcoesSkeleton = () => (
  <main className={styles.page} aria-busy="true">
    <Skeleton variant="text" width={270} height={44} />
    <Skeleton variant="text" width={480} height={26} />
    <section className={styles.filtersPanel}>
      <div className={styles.skeletonFilters}>
        {Array.from({ length: 5 }, (_, index) => (
          <Skeleton key={index} variant="rounded" height={40} />
        ))}
      </div>
    </section>
    {Array.from({ length: 3 }, (_, index) => (
      <section key={index} className={styles.actionCard}>
        <Skeleton variant="text" width="48%" height={32} />
        <Skeleton variant="text" width="72%" height={24} />
        <Skeleton height={110} />
      </section>
    ))}
  </main>
);

const AcoesDisponiveis = () => {
  const navigate = useNavigate();
  const isMobile = useMediaQuery("(max-width:767px)");
  const [filtersOpen, setFiltersOpen] = useState(!isMobile);
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [menuAction, setMenuAction] = useState<AcaoDisponivelInscricao | null>(
    null,
  );
  const [detailAction, setDetailAction] =
    useState<AcaoDisponivelInscricao | null>(null);
  const [orientationAction, setOrientationAction] =
    useState<AcaoQualificacao | null>(null);
  const [loadingOrientation, setLoadingOrientation] = useState(false);
  const [selectedTurma, setSelectedTurma] =
    useState<TurmaDisponivelInscricaoDetalhe | null>(null);
  const [confirmedTurma, setConfirmedTurma] =
    useState<TurmaDisponivelInscricaoDetalhe | null>(null);
  const [loadingTurma, setLoadingTurma] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const {
    loading,
    loadingFiltros,
    loadingInitial,
    msgError,
    acoes,
    tipologias,
    modalidades,
    temas,
    currentPage,
    perPage,
    totalPages,
    filtros,
    setCurrentPage,
    setPerPage,
    atualizarFiltro,
    pesquisar,
    alterarOrdenacao,
    limparFiltros,
  } = useAcoesDisponiveis();

  const totalTurmas = useMemo(
    () =>
      acoes.reduce(
        (total, acao) => total + (acao.turmas_disponiveis?.length ?? 0),
        0,
      ),
    [acoes],
  );
  const closeMenu = () => {
    setMenuAnchor(null);
    setMenuAction(null);
  };

  const openSingleTurma = async (acao: AcaoDisponivelInscricao) => {
    const resumo = acao.turmas_disponiveis?.[0];
    if (!resumo) return;
    try {
      setLoadingTurma(true);
      const response = await findTurmasDisponiveisAcao(String(acao.id));
      const turma =
        response?.data?.turmas?.find((item) => item.id === resumo.id) ?? resumo;
      setSelectedTurma(turma as TurmaDisponivelInscricaoDetalhe);
    } catch (error) {
      setSuccessMessage(
        error instanceof Error
          ? error.message
          : "Não foi possível carregar a turma.",
      );
    } finally {
      setLoadingTurma(false);
    }
  };

  const confirmInscricao = async () => {
    if (!selectedTurma) return;
    try {
      setConfirming(true);
      const response = await realizarInscricaoTurma(selectedTurma.id);
      if (!response?.success)
        throw new Error(
          response?.message || "Não foi possível realizar a inscrição.",
        );
      setSuccessMessage(
        `Inscrição Confirmada! Você está inscrito(a) na turma ${selectedTurma.nome}.`,
      );
      setConfirmedTurma(selectedTurma);
      setSelectedTurma(null);
    } catch (error) {
      setSuccessMessage(
        error instanceof Error
          ? error.message
          : "Não foi possível realizar a inscrição.",
      );
    } finally {
      setConfirming(false);
    }
  };

  const handlePrimaryAction = (acao: AcaoDisponivelInscricao) => {
    const turmas = acao.turmas_disponiveis ?? [];
    if (acao.possui_inscricao) return navigate("/gerenciar-inscricoes");
    if (turmas.length > 1) return navigate("/orientacao-acao?id=" + acao.id);
    void openSingleTurma(acao);
  };

  const openOrientations = async () => {
    if (!menuAction) return;

    try {
      setLoadingOrientation(true);
      setOrientationAction(await findAcaoQualificacao(String(menuAction.id)));
      closeMenu();
    } catch (error) {
      setSuccessMessage(
        error instanceof Error
          ? error.message
          : "Não foi possível carregar as orientações da ação.",
      );
    } finally {
      setLoadingOrientation(false);
    }
  };

  if (loading || loadingFiltros || loadingInitial) return <AcoesSkeleton />;

  return (
    <main className={styles.page}>
      <header className={styles.header}>
        <h1>Realizar Inscrição</h1>
        <p>
          Encontre a ação de qualificação desejada e inscreva-se em uma turma
          disponível.
        </p>
      </header>
      {successMessage && (
        <Alert
          severity={
            successMessage.startsWith("Inscrição Confirmada")
              ? "success"
              : "error"
          }
          className={styles.feedback}
          onClose={() => setSuccessMessage(null)}
        >
          {successMessage}
        </Alert>
      )}
      {confirmedTurma && (
        <section className={styles.confirmedCard}>
          <CheckCircleOutline />
          <div>
            <h2>Inscrição Confirmada!</h2>
            <p>
              Você está inscrito(a) na turma{" "}
              <strong>{confirmedTurma.nome}</strong>.
            </p>
            <span>
              <CalendarMonthOutlined />{" "}
              {formatarDataDbParaBr(confirmedTurma.data_inicio)} a{" "}
              {formatarDataDbParaBr(confirmedTurma.data_fim)}
            </span>
          </div>
          <Button
            variant="contained"
            className={stylesButton.buttonSoftPrimary}
            onClick={() => navigate("/gerenciar-inscricoes")}
          >
            Ver minhas inscrições
          </Button>
        </section>
      )}
      <section className={styles.filtersPanel}>
        <button
          type="button"
          className={styles.mobileFiltersTrigger}
          onClick={() => setFiltersOpen((open) => !open)}
        >
          <span>
            <TuneOutlined /> Filtros de busca
          </span>
          {filtersOpen ? <ExpandLessOutlined /> : <ExpandMoreOutlined />}
        </button>
        {filtersOpen && (
          <div className={styles.filtersGrid}>
            <TextField
              size="small"
              variant="standard"
              placeholder="Nome da ação"
              value={filtros.searchNome}
              onChange={(event) =>
                atualizarFiltro("searchNome", event.target.value)
              }
              onKeyDown={(event) => event.key === "Enter" && pesquisar()}
              InputProps={{
                className: stylesInput.inputSoft,
                disableUnderline: true,
                startAdornment: (
                  <SearchOutlined className={styles.searchIcon} />
                ),
              }}
            />
            <SearchSelect
              label=""
              placeholder="Tipo da ação"
              options={tipologias}
              value={filtros.searchTipoAcao}
              onChange={(value) => atualizarFiltro("searchTipoAcao", value)}
              paperClassname={stylesInput.paper}
              popperClassname={stylesInput.popper}
              labelClassname={stylesInput.labelSelect}
              inputClassname={stylesInput.inputSoft}
            />
            <SearchSelect
              label=""
              placeholder="Tema"
              options={temas}
              value={filtros.tema}
              onChange={(value) => atualizarFiltro("tema", value)}
              paperClassname={stylesInput.paper}
              popperClassname={stylesInput.popper}
              labelClassname={stylesInput.labelSelect}
              inputClassname={stylesInput.inputSoft}
            />
            <SearchSelect
              label=""
              placeholder="Modalidade"
              options={modalidades}
              value={filtros.searchModalidade}
              onChange={(value) => atualizarFiltro("searchModalidade", value)}
              paperClassname={stylesInput.paper}
              popperClassname={stylesInput.popper}
              labelClassname={stylesInput.labelSelect}
              inputClassname={stylesInput.inputSoft}
            />
            <TextField
              size="small"
              type="date"
              variant="standard"
              value={filtros.searchPeriodoInicio}
              onChange={(event) =>
                atualizarFiltro("searchPeriodoInicio", event.target.value)
              }
              InputProps={{
                className: stylesInput.inputSoft,
                disableUnderline: true,
              }}
            />
            <TextField
              size="small"
              type="date"
              variant="standard"
              value={filtros.searchPeriodoFim}
              onChange={(event) =>
                atualizarFiltro("searchPeriodoFim", event.target.value)
              }
              InputProps={{
                className: stylesInput.inputSoft,
                disableUnderline: true,
              }}
            />
            <div className={styles.filterActions}>
              <Button
                variant="text"
                className={stylesButton.buttonInlineLink}
                onClick={limparFiltros}
              >
                Limpar
              </Button>
              <Button
                variant="contained"
                className={stylesButton.buttonSoftPrimary}
                onClick={pesquisar}
              >
                Filtrar
              </Button>
            </div>
          </div>
        )}
      </section>
      {msgError && (
        <Alert severity="error" className={styles.feedback}>
          {msgError}
        </Alert>
      )}
      <section className={styles.listHeader}>
        <div>
          <CalendarMonthOutlined />
          <strong>{acoes.length} ações de capacitação encontradas</strong>
          <span>{totalTurmas} turmas disponíveis nesta página</span>
        </div>
        <div className={stylesManagement.orderControl}>
          <label htmlFor="acoes-order">Ordenar por</label>
          <TextField
            id="acoes-order"
            select
            size="small"
            value={filtros.ordenacao}
            onChange={(event) => alterarOrdenacao(event.target.value)}
          >
            {orderOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
        </div>
      </section>
      <section className={styles.cardsList}>
        {acoes.length === 0 ? (
          <div className={styles.emptyState}>
            <InfoOutlined />
            <h2>Nenhuma ação disponível foi encontrada.</h2>
            <p>
              Tente ajustar os filtros ou consulte novamente em outro momento.
            </p>
            <Button
              variant="outlined"
              className={stylesButton.buttonSoftOutline}
              onClick={limparFiltros}
            >
              Limpar filtros
            </Button>
          </div>
        ) : (
          acoes.map((acao) => {
            const turmas = acao.turmas_disponiveis ?? [];
            const hasMoreTurmas = turmas.length > 1;
            return (
              <article key={acao.id} className={styles.actionCard}>
                <div className={styles.actionSummary}>
                  <div className={styles.actionIcon}>
                    <SchoolOutlined />
                  </div>
                  <div className={styles.actionContent}>
                    <span>Ação de capacitação</span>
                    <h2>{acao.nome}</h2>
                    <div className={styles.actionFacts}>
                      <span>
                        <SchoolOutlined /> {acao.modalidade || "-"}
                      </span>
                      <span>
                        <InfoOutlined /> {acao.tipologia || "-"}
                      </span>
                      <span>
                        <CalendarMonthOutlined />{" "}
                        {formatarDataDbParaBr(acao.data_inicio)} a{" "}
                        {formatarDataDbParaBr(acao.data_fim)}
                      </span>
                    </div>
                  </div>
                </div>
                <div className={styles.turmasArea}>
                  <strong>
                    {turmas.length}{" "}
                    {turmas.length === 1
                      ? "turma disponível"
                      : "turmas disponíveis"}
                  </strong>
                  {turmas.slice(0, 5).map((turma) => (
                    <div key={turma.id} className={styles.turmaRow}>
                      <GroupsOutlined />
                      <span>{getTurmaLabel(turma)}</span>
                      {getTurmaDate(turma) && (
                        <small>
                          <CalendarMonthOutlined />{" "}
                          {formatarDataDbParaBr(getTurmaDate(turma))}
                        </small>
                      )}
                    </div>
                  ))}
                  {turmas.length > 5 && (
                    <small className={styles.moreTurmas}>
                      + {turmas.length - 5} outras turmas
                    </small>
                  )}
                </div>
                <div className={styles.cardActions}>
                  <IconButton
                    aria-label="Mais opções"
                    onClick={(event) => {
                      setMenuAnchor(event.currentTarget);
                      setMenuAction(acao);
                    }}
                  >
                    <MoreVertOutlined />
                  </IconButton>
                  {acao.possui_inscricao ? (
                    <Button
                      variant="contained"
                      className={stylesButton.buttonSoftPrimary}
                      onClick={() => navigate("/gerenciar-inscricoes")}
                    >
                      Ver minha inscrição
                    </Button>
                  ) : (
                    <Button
                      variant="contained"
                      className={stylesButton.buttonSoftPrimary}
                      disabled={turmas.length === 0 || loadingTurma}
                      onClick={() => handlePrimaryAction(acao)}
                    >
                      {hasMoreTurmas ? "Ver turmas" : "Inscrever-se"}
                    </Button>
                  )}
                </div>
              </article>
            );
          })
        )}
      </section>
      <footer className={stylesManagement.pagination}>
        <div className={styles.paginationSelect}>
          <span>Resultados por página</span>
          <SelectField
            label=""
            value={String(perPage)}
            onChange={(value) => {
              setPerPage(Number(value));
              setCurrentPage(1);
            }}
            options={[{ nome: "10" }, { nome: "20" }]}
            inputClassName={stylesInput.inputSoft}
            disableClearable
            disableTyping
          />
        </div>
        <div className={styles.simplePagination}>
          <Button
            disabled={currentPage === 1}
            onClick={() => setCurrentPage(currentPage - 1)}
          >
            Anterior
          </Button>
          <span>
            Página {currentPage} de {totalPages}
          </span>
          <Button
            disabled={currentPage === totalPages}
            onClick={() => setCurrentPage(currentPage + 1)}
          >
            Próxima
          </Button>
        </div>
      </footer>
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={closeMenu}
      >
        <MenuItem
          onClick={() => {
            setDetailAction(menuAction);
            closeMenu();
          }}
        >
          <VisibilityOutlined fontSize="small" /> Ver detalhes da ação
        </MenuItem>
        <MenuItem
          onClick={() => {
            void openOrientations();
          }}
        >
          <MenuBookOutlined fontSize="small" /> Consultar orientações
        </MenuItem>
      </Menu>
      <Dialog
        open={Boolean(detailAction)}
        onClose={() => setDetailAction(null)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>{detailAction?.nome}</DialogTitle>
        <DialogContent dividers className={styles.detailDialog}>
          <Detail label="Descrição" value={detailAction?.observacoes} />
          <Detail label="Objetivo" value={(detailAction as any)?.objetivo} />
          <Detail
            label="Público"
            value={
              (detailAction as any)?.publico_alvo ??
              (detailAction as any)?.publico
            }
          />
          <Detail label="Tipologia" value={detailAction?.tipologia} />
          <Detail
            label="Tema"
            value={detailAction?.temas
              ?.map((tema) => String(tema.tema_id))
              .join(", ")}
          />
          <Detail label="Modalidade" value={detailAction?.modalidade} />
          <Detail
            label="Carga horária"
            value={
              (detailAction as any)?.carga_horaria
                ? `${(detailAction as any).carga_horaria} horas`
                : undefined
            }
          />
          <Detail
            label="Período"
            value={
              detailAction
                ? `${formatarDataDbParaBr(detailAction.data_inicio)} a ${formatarDataDbParaBr(detailAction.data_fim)}`
                : undefined
            }
          />
          {detailAction?.arquivo_path && (
            <a
              href={detailAction.arquivo_path}
              target="_blank"
              rel="noreferrer"
            >
              Abrir documento ou convite
            </a>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailAction(null)}>
            Voltar à listagem
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={Boolean(orientationAction) || loadingOrientation}
        onClose={() => {
          if (!loadingOrientation) setOrientationAction(null);
        }}
        fullWidth
        maxWidth="xl"
      >
        <DialogTitle>Orientações da ação</DialogTitle>
        <DialogContent dividers className={styles.orientationDialog}>
          {loadingOrientation ? (
            <div className={styles.orientationLoading}>
              <CircularProgress size={30} />
            </div>
          ) : (
            <OrientationPreview action={orientationAction} />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOrientationAction(null)}>Fechar</Button>
        </DialogActions>
      </Dialog>
      <ConfirmDialog
        open={Boolean(selectedTurma)}
        loading={confirming}
        onClose={() => setSelectedTurma(null)}
        onConfirm={confirmInscricao}
        title="Confirmar inscrição"
        message={`Deseja confirmar sua inscrição na turma "${selectedTurma?.nome ?? ""}"?`}
        titleButtonConfirm="Confirmar inscrição"
        titleLoadButton="Confirmando..."
      />
    </main>
  );
};

const Detail = ({ label, value }: { label: string; value?: string | null }) =>
  value ? (
    <p>
      <strong>{label}:</strong> {value}
    </p>
  ) : null;

const OrientationPreview = ({
  action,
}: {
  action: AcaoQualificacao | null;
}) => {
  const apiPath = process.env.REACT_APP_API_URL?.replace("/api", "") || "";
  const arquivoPath = action?.arquivo_path;
  const arquivoUrl = arquivoPath
    ? arquivoPath.startsWith("http://") || arquivoPath.startsWith("https://")
      ? arquivoPath
      : apiPath + arquivoPath
    : "";
  const isPdf = arquivoUrl.toLowerCase().split("?")[0].endsWith(".pdf");

  if (!arquivoUrl) {
    return (
      <div className={styles.orientationEmpty}>
        Esta ação não possui arquivo complementar.
      </div>
    );
  }

  return isPdf ? (
    <iframe
      src={arquivoUrl}
      title="Orientações da ação"
      className={styles.orientationPdf}
    />
  ) : (
    <img
      src={arquivoUrl}
      alt="Orientações da ação"
      className={styles.orientationImage}
    />
  );
};

export default AcoesDisponiveis;
